<template>
  <v-layout column>
    <object-form-activities-tab-navbar>
      <v-btn small text icon color="accent" @click="exitActivityCreate">
        <v-icon>arrow_back_ios</v-icon>
      </v-btn>

      <v-flex class="subheading font-weight-bold">
        {{ $t("NewActivity") }}
      </v-flex>

      <list-edition-toolbar
        :buttons="toolbarButtons"
        @save="dueDateFilled"
        horizontal-alignment="right"
      />

      <toggle-buttons
        v-model="mode"
        :buttons="toggleButtons"
        style="margin: 6px 8px"
      />
    </object-form-activities-tab-navbar>

    <activity-create-form
      v-show="!loading && mode === 'details'"
      :new-activity="newActivity"
      :objectLayer="objectLayer"
      :objectData="objectData"
    />

    <tasks-table v-if="mode === 'tasks'" :tasks="newActivity.tasks" />
    <div v-if="loading" style="position:relative; width: 100%; height: 100px">
      <loading-spinner
        :active.sync="loading"
        :is-full-page="false"
        color="#4fa2b7"
        background-color="transparent"
      />
    </div>
    <app-dialog-box
      :open="warningDialog"
      :title="$t('Warning')"
      :content="$t('ActivityWarning')"
      :main-action-label="$t('No')"
      @mainAction="warningDialog = false"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="confirmWarning"
    />
  </v-layout>
</template>

<script>
import { mapActions } from "vuex";
import ObjectFormActivitiesTabNavbar from "@/components/Home/Forms/_SpecialTabs/Activities/ActivitiesTabNavbar";
import InputField from "@/components/Home/Forms/_Molecules/InputField";
import ListEditionToolbar from "@/components/Home/Forms/_Molecules/ListEditionToolbar";
import ActivityCreateForm from "./ActivityCreateForm";
import TasksTable from "./ActivityCreateTasks";
import ToggleButtons from "@/components/_Atoms/AppToggleButtons";
import LoadingSpinner from "vue-loading-overlay";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";

export default {
  name: "ObjectFormActivityCreate",

  components: {
    ToggleButtons,
    TasksTable,
    ActivityCreateForm,
    ListEditionToolbar,
    InputField,
    ObjectFormActivitiesTabNavbar,
    LoadingSpinner,
    AppDialogBox
  },

  props: {
    /**
     * UUID of selected Object
     */
    objectUuid: String,
    /**
     * Layer of selected Object
     */
    objectLayer: String,
    /**
     * Selected object data with all its properies (tp_height etc...)
     */
    objectData: Object
  },

  data() {
    return {
      newActivity: {
        title: undefined,
        description: null,
        due_date: null,
        /*dependencies: [],*/

        tasks: null,
        /**
         * auto_approval default mode
         */
        auto_approval: "automatic"
      },
      loading: false,
      mode: "details",
      warningDialog: false
    };
  },

  computed: {
    /**
     * Button property definitions loaded in listToolBar component.
     */
    toolbarButtons() {
      return [
        {
          id: "save",
          icon: "save",
          tooltip: this.$i18n.t("Save"),
          active: true,
          disabled: !this.newActivity.title
        }
      ];
    },
    /**
     * Buttons to see details of activity
     * and tasks table
     */
    toggleButtons() {
      return [
        { value: "details", text: this.$i18n.tc("Detail", 2) },
        {
          value: "tasks",
          text: this.$i18n.tc("Task", 2),
          disabled: this.newActivity.tasks === null
        }
      ];
    }
  },

  methods: {
    ...mapActions(["addActivity", "addTask"]),
    /**
     * emits close with uuid, after saving an activity, user is redirected into tasks list table.
     */
    exitActivityCreate(uuid) {
      this.$emit("close", uuid);
    },
    /**
     * if there is no date assigned, user will be prompted if he wishes to continue
     */
    dueDateFilled() {
      if (!this.newActivity.due_date) {
        this.warningDialog = true;
        return;
      }
      this.save();
    },
    /**
     * in case user wishes to continue an activity is created and dialog closes
     */
    confirmWarning() {
      this.save();
      this.warningDialog = false;
    },
    /**
     * Atributes an object_uuid to a new activity
     * Atributes an object_layer to a new activity
     * When adding an activity, addActivity executes, if the new activity has tasks it will add one by one, but only the ones that include
     * the properties in objectData.
     */
    async save() {
      this.newActivity.object_uuid = this.objectUuid;
      this.newActivity.object_type = this.objectLayer;
      try {
        this.loading = true;

        let activity = await this.addActivity(this.newActivity);
        if (this.newActivity.tasks && this.newActivity.tasks.length > 0) {
          for (let task of this.newActivity.tasks) {
            if (task.object_type[this.objectLayer]) {
              await this.saveTasks(task, activity.uuid);
            }
          }
        }
        this.exitActivityCreate(activity.uuid);
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },
    /**
     * @param {Object} newTask - task object properties.
     * @param {string} activityUuid - activityUUID to associate the newly created task.
     *
     * Executes addTask request.
     */
    async saveTasks(newTask, activityUuid) {
      newTask.activity_uuid = this.activityUuid;
      if (!newTask.description) {
        delete newTask.description;
      }
      if (!newTask.user_assignee_id) {
        delete newTask.user_assignee_id;
      }
      if (newTask.uuid) {
        delete newTask.uuid;
      }

      try {
        await this.addTask({
          activityUUID: activityUuid,
          newTaskData: newTask
        });
      } catch (e) {
        console.log(e);
      }
    }
  }
};
</script>

<style scoped>
.activity-form-container {
  border-radius: 2px;
  box-shadow: -0.5px 0.5px 2px rgba(0, 0, 0, 0.5);
  padding-top: 10px;
  padding-bottom: 20px;
  margin-top: 6px;
}
</style>



// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Activities/ActivityCreate/ActivityCreate.vue